// src/store/teamStore.js
import { create } from 'zustand';
import { teamService } from '../api/services/teams';
import { useToastStore } from '../components/UI/Toast';

const useTeamStore = create((set, get) => ({
    teams: [],
    currentTeam: null,
    members: [],
    teamWebsites: [],
    loading: false,
    error: null,

    // Načtení všech týmů
    fetchTeams: async () => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.getTeams();
            set({ teams: data, loading: false });
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Nepodařilo se načíst týmy',
                loading: false
            });
            throw error;
        }
    },

    // Vytvoření nového týmu
    createTeam: async (teamData) => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.createTeam(teamData);
            set({
                teams: [...get().teams, data],
                loading: false
            });
            useToastStore.getState().addToast('Tým byl úspěšně vytvořen', 'success');
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Vytvoření týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Vytvoření týmu selhalo', 'danger');
            throw error;
        }
    },

    // Načtení detailu týmu včetně členů a webů
    fetchTeamDetail: async (teamId) => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.getTeamDetail(teamId);
            set({
                currentTeam: data,
                members: data.members || [],
                teamWebsites: data.websites || [],
                loading: false
            });
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Nepodařilo se načíst detail týmu',
                loading: false
            });
            throw error;
        }
    },

    // Aktualizace týmu
    updateTeam: async (teamId, teamData) => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.updateTeam(teamId, teamData);
            set({
                teams: get().teams.map(team => team.id === teamId ? data : team),
                currentTeam: data,
                loading: false
            });
            useToastStore.getState().addToast('Tým byl úspěšně aktualizován', 'success');
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Aktualizace týmu selhala',
                loading: false
            });
            useToastStore.getState().addToast('Aktualizace týmu selhala', 'danger');
            throw error;
        }
    },

    // Smazání týmu
    deleteTeam: async (teamId) => {
        set({ loading: true, error: null });
        try {
            await teamService.deleteTeam(teamId);
            set({
                teams: get().teams.filter(team => team.id !== teamId),
                currentTeam: get().currentTeam?.id === teamId ? null : get().currentTeam,
                loading: false
            });
            useToastStore.getState().addToast('Tým byl úspěšně smazán', 'success');
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Smazání týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Smazání týmu selhalo', 'danger');
            throw error;
        }
    },

    // Přidání člena do týmu
    addTeamMember: async (teamId, userData) => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.addTeamMember(teamId, userData);
            set({
                members: [...get().members, data],
                loading: false
            });
            useToastStore.getState().addToast('Uživatel byl úspěšně přidán do týmu', 'success');
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Přidání uživatele do týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Přidání uživatele do týmu selhalo', 'danger');
            throw error;
        }
    },

    // Odebrání člena z týmu
    removeTeamMember: async (teamId, userId) => {
        set({ loading: true, error: null });
        try {
            await teamService.removeTeamMember(teamId, userId);
            set({
                members: get().members.filter(member => member.id !== userId),
                loading: false
            });
            useToastStore.getState().addToast('Uživatel byl úspěšně odebrán z týmu', 'success');
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Odebrání uživatele z týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Odebrání uživatele z týmu selhalo', 'danger');
            throw error;
        }
    },

    // Přiřazení webu k týmu
    assignWebsiteToTeam: async (teamId, websiteId) => {
        set({ loading: true, error: null });
        try {
            const { data } = await teamService.assignWebsiteToTeam(teamId, websiteId);
            set({
                teamWebsites: [...get().teamWebsites, data],
                loading: false
            });
            useToastStore.getState().addToast('Web byl úspěšně přiřazen k týmu', 'success');
            return data;
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Přiřazení webu k týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Přiřazení webu k týmu selhalo', 'danger');
            throw error;
        }
    },

    // Odebrání webu z týmu
    removeWebsiteFromTeam: async (teamId, websiteId) => {
        set({ loading: true, error: null });
        try {
            await teamService.removeWebsiteFromTeam(teamId, websiteId);
            set({
                teamWebsites: get().teamWebsites.filter(website => website.id !== websiteId),
                loading: false
            });
            useToastStore.getState().addToast('Web byl úspěšně odebrán z týmu', 'success');
        } catch (error) {
            set({
                error: error.response?.data?.message || 'Odebrání webu z týmu selhalo',
                loading: false
            });
            useToastStore.getState().addToast('Odebrání webu z týmu selhalo', 'danger');
            throw error;
        }
    }
}));

export default useTeamStore;